<template>
	<nav class="m-breadcrumb" :class="`m-breadcrumb--${variant}`" aria-label="breadcrumb">
		<template v-if="normalizedItems.length">
			<template v-for="(item, index) in normalizedItems" :key="`${item.label}-${index}`">
				<span v-if="index > 0" class="m-breadcrumb__separator">{{ separator }}</span>
				<span
					class="m-breadcrumb__item"
					:class="item.disabled ? 'm-breadcrumb__item--current' : 'm-breadcrumb__item--link'"
					@click="handleItemClick(item, index)"
				>
					{{ item.label }}
				</span>
			</template>
		</template>

		<template v-else>
			<span v-if="showHome" class="m-breadcrumb__item m-breadcrumb__item--link" @click="toHome">
				{{ homeName }}
			</span>

			<template v-if="displayBackRouteNameRaw">
				<span class="m-breadcrumb__separator">{{ separator }}</span>
				<span class="m-breadcrumb__item m-breadcrumb__item--link" @click="toBack">
					{{ displayBackRouteName }}
				</span>
			</template>

			<template v-if="displayCurrentNameRaw">
				<span class="m-breadcrumb__separator">{{ separator }}</span>
				<span class="m-breadcrumb__item m-breadcrumb__item--current">
					{{ displayCurrentName }}
				</span>
			</template>
		</template>
	</nav>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import eventBus, { EVENT_TYPES } from "~/utils/eventBus";

const { t, te } = useI18n();

const BREADCRUMB_LABEL_ALIASES = {
	首頁: "common.breadcrumb.home",
	首页: "common.breadcrumb.home",
	導師服務: "common.breadcrumb.tutor",
	导师服务: "common.breadcrumb.tutor",
	老師服務: "common.teacherService.breadcrumb",
	老师服务: "common.teacherService.breadcrumb",
	老師詳情: "teacherDetail.pc.breadcrumb",
	老师详情: "teacherDetail.pc.breadcrumb",
	AI對話: "common.breadcrumb.aiChat",
	AI对话: "common.breadcrumb.aiChat",
	聊天記錄: "common.breadcrumb.chatHistory",
	聊天记录: "common.breadcrumb.chatHistory",
	線上測試: "common.breadcrumb.onlineTest",
	线上测试: "common.breadcrumb.onlineTest",
};

function translateBreadcrumbLabel(key) {
	if (!key || typeof key !== "string") return key;
	const aliasKey = BREADCRUMB_LABEL_ALIASES[key];
	if (aliasKey && te(aliasKey)) return t(aliasKey);
	return te(key) ? t(key) : key;
}

const props = defineProps({
	items: {
		type: Array,
		default: () => [],
	},
	name: {
		type: String,
		default: "",
	},
	homeName: {
		type: String,
		default: "首頁",
	},
	homePath: {
		type: String,
		default: "/",
	},
	backRouteName: {
		type: String,
		default: "",
	},
	backPath: {
		type: String,
		default: "",
	},
	showHome: {
		type: Boolean,
		default: true,
	},
	showBack: {
		type: Boolean,
		default: true,
	},
	separator: {
		type: String,
		default: ">",
	},
	variant: {
		type: String,
		default: "default",
	},
});

const emit = defineEmits(["item-click"]);

const route = useRoute();
const router = useRouter();
const backRouteNameRef = ref("");

const normalizedItems = computed(() =>
	props.items
		.map(item => {
			if (typeof item === "string") {
				return {
					label: translateBreadcrumbLabel(item),
					rawLabel: item,
					disabled: false,
				};
			}

			return {
				...item,
				rawLabel: item.label || item.name || "",
				label: translateBreadcrumbLabel(item.label || item.name || ""),
				disabled: Boolean(item.disabled || item.current),
			};
		})
		.filter(item => item.label)
);

const displayCurrentNameRaw = computed(() => props.name || route.meta?.breadcrumbName || "");

const displayCurrentName = computed(() => translateBreadcrumbLabel(displayCurrentNameRaw.value));

const displayBackRouteNameRaw = computed(() => {
	if (!props.showBack) return "";
	if (props.backRouteName) return props.backRouteName;
	return backRouteNameRef.value;
});

const displayBackRouteName = computed(() =>
	translateBreadcrumbLabel(displayBackRouteNameRaw.value)
);

onMounted(() => {
	if (!import.meta.client || props.backRouteName) return;
	const { routes, history } = router.options;
	backRouteNameRef.value = findMostMatchedRoute(routes, history.state.back);
});

function findMostMatchedRoute(routes, pathToMatch) {
	if (!pathToMatch) return "";
	let mostMatchedRoute = null;
	let maxMatchLength = 0;

	routes.forEach(item => {
		const match = pathToMatch.match(new RegExp(`^${item.path.replace(/:\\w+/g, "([^/]+)")}`));
		if (match && match[0].length >= maxMatchLength) {
			mostMatchedRoute = item;
			maxMatchLength = match[0].length;
		}
	});

	return mostMatchedRoute?.meta?.breadcrumbName || "";
}

function toHome() {
	return navigateTo(props.homePath);
}

function toBack() {
	if (props.backPath) {
		return navigateTo(props.backPath);
	}
	router.go(-1);
}

function handleItemClick(item, index) {
	if (item.disabled) return;
	emit("item-click", item, index);

	if (item.action === "openUserCenterDrawer") {
		eventBus.emit(EVENT_TYPES.OPEN_USER_CENTER_DRAWER);
		return;
	}

	if (item.path) {
		return navigateTo(item.path);
	}

	if (item.to) {
		return navigateTo(item.to);
	}
}
</script>

<style lang="scss" scoped>
.m-breadcrumb {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	font-size: 24px;
	line-height: 1.5;
	color: #8a8f99;

	&__item {
		&--link {
			color: #8a8f99;
			font-weight: 400;
			cursor: pointer;
		}

		&--current {
			color: #22262a;
			font-weight: 600;
		}
	}

	&__separator {
		color: #8a8f99;
	}

	&--user-center {
		gap: 10px;
		font-size: 30px;
		line-height: 38px;

		.m-breadcrumb__item--link {
			color: #000000;
			font-weight: 500;
		}

		.m-breadcrumb__item--current {
			color: #f2af19;
		}

		.m-breadcrumb__separator {
			color: #a0a0a0;
		}
	}
}
</style>
